import { ApiEndpoints } from "@shared/api/api-endpoints";
import {
  authApiClient,
  onboardingApiClient,
  bxApiClient,
  cxApiClient,
  mxApiClient,
} from "@shared/api/clients/apiClientFactory";
import { QueryStringParameters } from "@shared/api/url-builder";
import { PAGE_SIZE } from "@shared/utils/constants";
import { transformJsonToObject } from "@shared/utils/jsonToObjectTransformer";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { ConfigMapKeys } from "../configMap.constants";
import { tarrifFilters } from "./components/plan-tariffs/components/Tarrif/types";
import {
  ActiveUtilityServcies,
  AddHolidayPayload,
  AllUtilityServcies,
  CategoryOption,
  choiceFieldOption,
  ChoicesApiResponse,
  ContractItems,
  CustomerServiceResponse,
  DashboardStats,
  DashboardSummaryResponse,
  DateFormatApiResponse,
  Department,
  DepartmentResponse,
  DepartmentRole,
  EntityFilters,
  EntityStatsResponse,
  EntityTypeApiResponse,
  GetItemsParams,
  HolidayApiResponse,
  IdTypeApiResponse,
  InviteUserPayload,
  LeaveType,
  MonthlySummaryItem,
  NumberFormatApiResponse,
  PaginatedResponse,
  PlanDashboardData,
  planFilters,
  PlanVersionHistoryResponse,
  RateDetail,
  RateTypeOption,
  RecentlyUpdatedPlan,
  RecentlyUpdatedRate,
  Role,
  RoleResponse,
  SeparatorApiResponse,
  ServiceCharge,
  SubcategoryOption,
  SubscriptionModuleItem,
  TariffListItem,
  TarrifDetails,
  TerritoryCountResponse,
  TerritoryMappingItem,
  UserData,
  userFilters,
  UserMgmtDashboardResponse,
  Utility,
  UtilityRevenueResponse,
  UtilityServiceApiResponse,
  WorkingHourApiResponse,
  WorkOrderServiceResponse,
} from "./types";

export interface ParameterResponse {
  id: number;
  name: string;
  description: string;
  entityType: string;
  entityTypeDisplay: string;
  dataType: string;
  dataTypeDisplay: string;
  valuesSource: string;
  valuesSourceDisplay: string;
  allowedValues: string[];
  valuesSourceId: number | null;
  unit: string | null;
  appliesToServices: string[];
  status: string;
  statusDisplay: string;
  remoteUtilityId: number;
  usageCount: number;
  createdDate: string;
  lastModifiedDate: string;
}

export interface GetAvailableResourcesParams {
  remoteUtilityId?: string | number;
  utilityService?: string;
  resourceTypeCode?: string;
  resourceSubtypeCode?: string;
}

export const getAvailableResources = async (
  params: GetAvailableResourcesParams = {},
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "resource-management/available",
    (qs) => {
      if (
        params.remoteUtilityId !== undefined &&
        params.remoteUtilityId !== null &&
        params.remoteUtilityId !== ""
      ) {
        qs.push("remote_utility_id", String(params.remoteUtilityId));
      }
      if (params.utilityService) {
        qs.push("utility_service", params.utilityService);
      }
      if (params.resourceTypeCode) {
        qs.push("resource_type_code", params.resourceTypeCode);
      }
      if (params.resourceSubtypeCode) {
        qs.push("resource_subtype_code", params.resourceSubtypeCode);
      }
    },
  );
  const response = await mxApiClient.get(url);
  return response.data;
};

export interface UpdateResourceUnitPayload {
  unit_code?: string;
  unitCode?: string;
  premise?: string;
}

export const updateResourceUnit = async (
  resourceId: string | number,
  payload: UpdateResourceUnitPayload,
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithPathVariables(
    "mx",
    "device-management/update-unit",
    [resourceId],
  );
  const body = {
    unit_code: payload.unit_code || payload.unitCode || "",
    premise: payload.premise || "",
  };
  const response = await mxApiClient.patch(url, body);
  return response.data;
};

export interface GetResourceUnitAssignmentParams {
  remoteUtilityId?: string | number;
  unitCode?: string;
  searchData?: string;
  utilityService?: string;
  isExport?: boolean;
  isHistory?: number;
}

export const getResourceUnitAssignments = async (
  params: GetResourceUnitAssignmentParams = {},
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "resource-unit-assignment",
    (qs) => {
      if (
        params.remoteUtilityId !== undefined &&
        params.remoteUtilityId !== null &&
        params.remoteUtilityId !== ""
      ) {
        qs.push("remote_utility_id", String(params.remoteUtilityId));
      }
      if (params.unitCode) {
        qs.push("unit_code", params.unitCode);
      }
      if (params.searchData) {
        qs.push("search_data", params.searchData);
      }
      if (params.utilityService) {
        qs.push("utility_service", params.utilityService);
      }
      if (params.isExport) {
        qs.push("is_export", "true");
      }
      if (params.isHistory !== undefined) {
        qs.push("is_history", String(params.isHistory));
      }
    },
  );
  const response = await mxApiClient.get(url);
  return response.data;
};

export interface ReplaceResourcePayload {
  old_resource_id?: number | string;
  oldResourceId?: number | string;
  new_resource_id?: number | string;
  newResourceId?: number | string;
}

export const replaceResource = async (
  payload: ReplaceResourcePayload,
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "resource-management/replace",
    () => {},
  );
  const body = {
    old_resource_id: payload.old_resource_id ?? payload.oldResourceId,
    new_resource_id: payload.new_resource_id ?? payload.newResourceId,
  };
  const response = await mxApiClient.patch(url, body);
  return response.data;
};

export const onboardingApi = {
  getAvailableResources: async (
    params: GetAvailableResourcesParams = {},
  ): Promise<any> => {
    return getAvailableResources(params);
  },
  updateResourceUnit: async (
    resourceId: string | number,
    payload: UpdateResourceUnitPayload,
  ): Promise<any> => {
    return updateResourceUnit(resourceId, payload);
  },
  getResourceUnitAssignments: async (
    params: GetResourceUnitAssignmentParams = {},
  ): Promise<any> => {
    return getResourceUnitAssignments(params);
  },
  replaceResource: async (payload: ReplaceResourcePayload): Promise<any> => {
    return replaceResource(payload);
  },
  getNscAddConsumers: async (
    remoteUtilityId: string | number,
    searchData?: string,
    page?: number,
    limit?: number,
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "nsc-add",
      (qs) => {
        qs.push("remote_utility_id", remoteUtilityId);
        if (searchData) qs.push("search_data", searchData);
        qs.push("is_extra_info", 1);
        qs.push("active_consumers", 1);
        qs.push("is_pagination_required", false);
        if (page) qs.push("page", page);
        if (limit) qs.push("limit", limit);
      },
    );
    const response = await cxApiClient.get(url);
    return response.data;
  },

  getUnitWiseConsumers: async (
    unitCode?: string,
    remoteUtilityId?: string | number,
    isCurrent?: number,
    searchData?: string,
    page?: number,
    limit?: number,
    isExport?: boolean,
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "unit-wise/consumer",
      (qs) => {
        if (unitCode) qs.push("unit_code", unitCode);
        if (remoteUtilityId) qs.push("remote_utility_id", remoteUtilityId);
        if (isCurrent !== undefined && isCurrent !== null)
          qs.push("is_current", isCurrent);
        if (searchData) qs.push("search_data", searchData);
        if (page) qs.push("page", page);
        if (limit) qs.push("limit", limit);
        if (isExport) qs.push("is_export", true);
      },
    );
    const response = await cxApiClient.get(url);
    return response.data;
  },

  updateAssignedServiceAddress: async (params: any): Promise<any> => {
    const { consumerId, ...payload } = params;
    const url = ApiEndpoints.createUrl(
      "cx",
      `assigned-service-address/${consumerId}`,
    );
    const response = await cxApiClient.put(url, payload);
    return response.data;
  },

  // Get all utilities
  getUtilities: async (tenantId: string): Promise<Utility[]> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "auth",
      "get-utility",
      (query) => {
        query.push("tenant_id", tenantId);
        query.push("page", "1");
      },
    );
    const response = await onboardingApiClient.get(url);
    return response.data.result ?? [];
  },

  getUtilityProgressData: async (tenantId: string): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "auth",
      "org-setup-progress",
      (query) => {
        query.push("tenant_id", tenantId);
      },
    );
    const response = await onboardingApiClient.get(url);
    return response.data.result ?? [];
  },

  getActiveUtilityService: async (
    utilityID: string,
  ): Promise<ActiveUtilityServcies[]> => {
    // ✅ Return array
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "auth",
      "utility/services",
      (query) => {
        query.push("utility_id", utilityID);
      },
    );

    const response = await onboardingApiClient.get(url);
    const rawData = transformJsonToObject<ActiveUtilityServcies[]>(
      response.data.result,
    );

    // ✅ Return filtered array directly
    return rawData.filter((item) => item.isActive);
  },

  getAllUtilityService: async (
    remoteUtilityId: string,
  ): Promise<AllUtilityServcies[]> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "auth",
      "utility/services",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
      },
    );
    const response = await onboardingApiClient.get(url);
    return transformJsonToObject<ActiveUtilityServcies[]>(response.data.result);
  },
  getSubscriptionData: async (
    remoteUtilityId: string,
    tenantId,
  ): Promise<SubscriptionModuleItem[]> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "auth",
      "subscription-map",
      (query) => {
        query.push("tenant_id", tenantId);
        query.push("utility_id", remoteUtilityId);
      },
    );
    const response = await onboardingApiClient.get(url);
    return transformJsonToObject<SubscriptionModuleItem[]>(
      response.data.result,
    );
  },

  createUtility: async (formData: FormData): Promise<Utility> => {
    const url = ApiEndpoints.createUrl("auth", "get-utility");

    // Debug form data
    for (const [key, value] of formData.entries()) {
      if (value instanceof File) {
        console.log(
          `${key}: File(${value.name}, ${value.type}, ${value.size})`,
        );
      } else {
        console.log(`${key}: ${value}`);
      }
    }

    const response = await onboardingApiClient.post(url, formData);
    return response.data ?? null;
  },

  updateUtility: async (formData: FormData, id: number): Promise<Utility> => {
    const url = ApiEndpoints.createUrlWithPathVariables("auth", "get-utility", [
      id,
    ]);
    const response = await onboardingApiClient.put(url, formData);
    return response.data ?? null;
  },

  utilityUnitChange: async (data: any): Promise<Utility> => {
    const id = data.id;
    console.log("utility at api", id);
    const url = ApiEndpoints.createUrlWithPathVariables(
      "auth",
      `meter-required/${id}`,
    );
    const response = await onboardingApiClient.put(url, data);
    return response.data ?? null;
  },
  updateSystemAdminAccess: async (data: any): Promise<Utility> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "auth",
      "subscription-map",
    );
    const response = await onboardingApiClient.put(url, data);
    return response.data ?? null;
  },
  updateUtilityStatus: async (data: any): Promise<Utility> => {
    const id = data.id;
    const url = ApiEndpoints.createUrlWithPathVariables(
      "auth",
      "utility/services",
    );
    const response = await onboardingApiClient.post(url, data);
    return response.data ?? null;
  },

  useUpdateContract: async (id: number, data: FormData): Promise<Utility> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "onboarding",
      `contract/${id}`,
    );
    const response = await onboardingApiClient.put(url, data);
    return response.data ?? null;
  },

  useDeleteContract: async (id: number): Promise<Utility> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "onboarding",
      `contract/${id}`,
    );
    const response = await onboardingApiClient.delete(url);
    return response.data ?? null;
  },

  toggleUtilityStatus: async (
    id: number,
    isActive: boolean,
  ): Promise<Utility> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "auth",
      `utility/${id}`,
    );
    const response = await onboardingApiClient.put(url, {
      is_active: isActive,
    });
    return response.data;
  },
  // Delete utility
  deleteUtility: async (id: number): Promise<void> => {
    const response = await onboardingApiClient.delete<void>(`/utilities/${id}`);
    return response.data;
  },
  // Get setup progress
  getSetupProgress: async (): Promise<{
    progress: number;
    completed_steps: string[];
  }> => {
    const response = await onboardingApiClient.get<{
      progress: number;
      completed_steps: string[];
    }>("/setup/progress");
    return response.data;
  },
  // Get utility dashboard summary
  getUtilityDashboardSummary: async (
    remoteUtilityId: string,
  ): Promise<DashboardSummaryResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "utility-detail/summary",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
      },
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },

  getUtilitySetup: async (remoteUtilityId: string): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "auth",
      `utility-setup/${remoteUtilityId}`,
      (query) => {
        query.push("setup_details", "true");
      },
    );
    const response = await authApiClient.get(url);
    return response.data;
  },
  getCategoriesData: async (remoteUtilityId: string): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "config-map/category_json",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("config_level", "category");
      },
    );
    const response = await onboardingApiClient.get(url);
    return response.data.result;
  },
  // Get utility dashboard summary
  getUtilityRevenue: async (
    remoteUtilityId: string,
  ): Promise<UtilityRevenueResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "revenue-details",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("details_required", "1");
      },
    );
    const response = await onboardingApiClient.get(url);
    return response.data.result;
  },
  getCustomerService: async (
    remoteUtilityId: string,
  ): Promise<CustomerServiceResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "consumer_details",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("details_required", "1");
      },
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },
  getWorkOrderService: async (
    remoteUtilityId: string,
  ): Promise<WorkOrderServiceResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "workorder-details",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("details_required", "1");
      },
    );
    const response = await onboardingApiClient.get(url);
    return response.data.result;
  },
  // Get utility dashboard summary
  getUtilityMgmtDashboard: async (
    remoteUtilityId: string,
  ): Promise<UserMgmtDashboardResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "auth",
      "user-dashboard",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("details_required", "1");
      },
    );
    const response = await onboardingApiClient.get(url);
    return response.data.result;
  },
  getUtilityMgmtList: async (
    remoteUtilityId: string,
    filters: userFilters = {},
  ): Promise<PaginatedResponse<UserData>> => {
    console.log("filters at api", filters);

    const url = ApiEndpoints.createUrlWithQueryParameters(
      "auth",
      "user-info",
      (query) => {
        // Add remote utility ID
        query.push("remote_utility_id", remoteUtilityId);

        // Add pagination (with defaults)
        query.push("page", (filters.page || 1).toString());
        query.push("limit", (filters.limit || 50).toString());

        // Add search if provided
        if (filters.search_data) {
          query.push("search_data", filters.search_data);
        }

        // Add category if provided
        if (filters.department) {
          query.push("department", filters.department);
        }
        if (filters.role) {
          query.push("role", filters.role);
        }

        // Add status filters if provided
        if (filters.status?.length) {
          filters.status.forEach((status) =>
            query.push("status", status.toString()),
          );
        }
      },
    );

    const response = await onboardingApiClient.get(url);
    const transformedResponse = transformJsonToObject<
      PaginatedResponse<UserData>
    >(response.data);
    return transformedResponse;
  },
  // getUtilityMgmtList: async (
  //   remoteUtilityId: string,filters: userFilters = {}
  // ): Promise<PaginatedResponse<UserData>> => {
  //   const url = ApiEndpoints.createUrlWithQueryParameters(
  //     "auth",
  //     "user-info",
  //     (query) => {
  //         if (filters.page) query.push('page', filters.page.toString());
  //     if (filters.limit) query.push('limit', filters.limit.toString());
  //     if (filters.search_data) query.push('search_data', filters.search_data);
  //     if (filters.category) query.push('category', filters.category);

  //       query.push("remote_utility_id", remoteUtilityId);
  //       query.push("page", "1");
  //       query.push("limit", "50");
  //        if (filters.status?.length) {
  //       filters.status.forEach(status => query.push('status', status.toString()));
  //     }
  //     }
  //   );
  //   const response = await onboardingApiClient.get(url);
  //   const transformedResponse = transformJsonToObject<PaginatedResponse<UserData>>(response.data);
  //   return transformedResponse;
  // },
  // getDepartments: async (remoteUtilityId: string): Promise<Department[]> => {
  //   const url = ApiEndpoints.createUrlWithQueryParameters(
  //     "onboarding",
  //     "config-map/department_json",
  //     (query) => {
  //       query.push("remote_utility_id", remoteUtilityId);
  //     }
  //   );
  //   const response = await onboardingApiClient.get<DepartmentResponse>(url);
  //   return response.data.result; // this is Department[]
  // },
  // getRoles: async (
  //   remoteUtilityId: string,
  //   department?: string
  // ): Promise<Role[]> => {
  //   const url = ApiEndpoints.createUrlWithQueryParameters(
  //     "auth",
  //     "role-configuration", // Use role-configuration instead of just "role"
  //     (query) => {
  //       query.push("remote_utility_id", remoteUtilityId);

  //       // Only add department if it's provided
  //       if (department) {
  //         query.push("department", department);
  //       }
  //     }
  //   );
  //   const response = await onboardingApiClient.get<RoleResponse>(url);
  //   return response.data.result;
  // },

  getDepartments: async (remoteUtilityId: string): Promise<Department[]> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "config-map/department_json",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
      },
    );
    const response = await onboardingApiClient.get<DepartmentResponse>(url);
    return response.data.result;
  },

  getRoles: async (
    remoteUtilityId: string,
    departments?: string[],
  ): Promise<Role[]> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "auth",
      "role-configuration",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);

        // Add each department as a separate query parameter
        if (departments && departments.length > 0) {
          departments.forEach((dept) => {
            query.push("department", dept);
          });
        }
      },
    );
    const response = await onboardingApiClient.get<RoleResponse>(url);
    return response.data.result;
  },

  inviteUser: async (data: InviteUserPayload): Promise<any> => {
    const url = ApiEndpoints.createUrlWithPathVariables("auth", "user-info");
    const response = await onboardingApiClient.post(url, data);
    return response.data;
  },
  updateInviteUser: async (
    id: number,
    data: InviteUserPayload,
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "auth",
      `user-info/${id}`,
    );
    const response = await onboardingApiClient.put(url, data);
    return response.data;
  },
  resendUser: async (payload: { email: string }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "auth",
      "resend/invite",
    );
    const response = await onboardingApiClient.post(url, payload);
    return response.data;
  },
  resendUserOrg: async (payload: { email: string }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "auth",
      "resend/invite",
    );
    const response = await onboardingApiClient.post(url, payload);
    return response.data;
  },
  getLeaveTypes: async (remoteUtilityId: string): Promise<LeaveType[]> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "onboarding",
      "choices/leaveType",
    );
    const response = await onboardingApiClient.get(url);
    return response.data.result;
  },
  addHoliday: async (data: AddHolidayPayload): Promise<any> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "onboarding",
      "holiday",
    );
    const response = await onboardingApiClient.post(url, data);
    return response.data;
  },
  updateHoliday: async (data: AddHolidayPayload, id: number): Promise<any> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "onboarding",
      `holiday/${id}`,
    );
    const response = await onboardingApiClient.put(url, data);
    return response.data;
  },

  deleteHoliday: async (id: number): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "holiday",
      (query) => {
        query.push("id", id);
      },
    );
    const response = await onboardingApiClient.delete(url);
    return response.data;
  },

  addWorkingHour: async (payload: any): Promise<any> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "onboarding",
      "add-working-hour",
    );
    const response = await onboardingApiClient.put(url, payload);
    return response.data;
  },
  getHolidayItems: async (
    remoteUtilityId: string,
    monthYear: string,
  ): Promise<HolidayApiResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "holiday",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId.toString());
        query.push("month", monthYear); // Example: "July 2025"
      },
    );
    const response = await onboardingApiClient.get(url); // GET not POST
    return response.data;
  },

  getWorkingItems: async (
    remoteUtilityId: string,
  ): Promise<WorkingHourApiResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "config-map/working_hour",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("is_pagination_required", "true");
        query.push("page", "1");
        query.push("limit", PAGE_SIZE);
      },
    );
    const response = await onboardingApiClient.get(url); // GET not POST
    return response.data;
  },

  getEntityTypes: async (
    isMaster: boolean = true,
  ): Promise<EntityTypeApiResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "auth",
      "entity-type",
      (query) => {
        query.push("is_master", isMaster ? "1" : "0");
      },
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },

  getDateFormatChoices: async (): Promise<DateFormatApiResponse> => {
    const url = ApiEndpoints.createUrl(
      "auth",
      "choices/num_format_date_element",
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },

  getSeparatorChoices: async (): Promise<SeparatorApiResponse> => {
    const url = ApiEndpoints.createUrl("auth", "choices/num_format_separator");
    const response = await onboardingApiClient.get(url);
    return response.data;
  },

  getEntityChoices: async (): Promise<ChoicesApiResponse> => {
    const url = ApiEndpoints.createUrl("onboarding", "choices/entity_type");
    const response = await onboardingApiClient.get(url);
    return response.data;
  },

  getTerritoryJson: async (
    remoteUtilityId: string,
    configLevel: string,
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "config-map/territory_json",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("config_level", configLevel);
      },
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },

  getConfigCode: async (entityName: string): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "generate-configuration-code",
      (query) => {
        query.push("entity_name", entityName);
      },
    );
    const response = await onboardingApiClient.get(url);
    return transformJsonToObject<any>(response.data);
  },
  getIdTypes: async (): Promise<IdTypeApiResponse> => {
    const url = ApiEndpoints.createUrl("auth", "choices/num_format_type");
    const response = await onboardingApiClient.get(url);
    return response.data;
  },

  createIdConfig: async (data: any): Promise<any> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "auth",
      "number-format",
    );
    const response = await onboardingApiClient.post(url, data);
    return response.data;
  },

  createContract: async (data: any): Promise<any> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "onboarding",
      "contract",
    );
    const response = await onboardingApiClient.post(url, data);
    return response.data;
  },

  getNumberFormats: async (
    utilityId: string,
    tab?: string,
    page?: number,
    limit?: number,
  ): Promise<NumberFormatApiResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "auth",
      "number-format",
      (query) => {
        query.push("utility_id", utilityId);
        if (tab === "master") query.push("is_master", "1");
        if (tab === "transaction") query.push("is_transaction", "1");
        if (page !== undefined) query.push("page", page.toString());
        if (limit !== undefined) query.push("limit", limit.toString());
      },
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },

  getUtilityServices: async (
    remoteUtilityId: string,
  ): Promise<UtilityServiceApiResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "auth",
      "utility/services",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
      },
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },

  // ======== ONBOARDING APIs (from branch) ========

  getRateTypes: async (): Promise<RateTypeOption[]> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "onboarding",
      "choices/rate_type",
    );
    const response = await onboardingApiClient.get(url);
    return transformJsonToObject<RateTypeOption[]>(response.data.result);
  },

  getBasisTypes: async (rate_type: Number): Promise<RateTypeOption[]> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "choices/basis_type",
      (query) => {
        query.push("rate_type", rate_type); // Example: filter by SEASONAL rate type
      },
    );
    const response = await onboardingApiClient.get(url);
    return transformJsonToObject<RateTypeOption[]>(response.data.result);
  },

  searchEntities: async (
    remote_utility_id: string,
    search_data: string,
    page: number = 1,
    limit: number = 10,
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "territory-mapping",
      (query) => {
        query.push("remote_utility_id", remote_utility_id);
        query.push("page", page.toString());
        query.push("limit", limit.toString());
        query.push("is_pagination_required", "1");
        query.push("search_data", search_data);
      },
    );
    const response = await onboardingApiClient.get(url);
    return transformJsonToObject<any>(response.data);
  },

  getBillFrequency: async (): Promise<choiceFieldOption[]> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "onboarding",
      "choices/billingFrequency",
    );
    const response = await onboardingApiClient.get(url);
    return transformJsonToObject<choiceFieldOption[]>(response.data.result);
  },
  getTerritoryPremiseStats: async (
    remoteUtilityId: string,
    territoryCode: string,
  ): Promise<DashboardStats> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "territory-premise-data",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("territory_code", territoryCode);
      },
    );
    const response = await onboardingApiClient.get(url);
    return transformJsonToObject<DashboardStats>(response.data);
  },

  getTaxType: async (): Promise<choiceFieldOption[]> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "onboarding",
      "choices/tax_type",
    );
    const response = await onboardingApiClient.get(url);
    return transformJsonToObject<choiceFieldOption[]>(response.data.result);
  },

  addRate: async (data: any): Promise<any> => {
    const url = ApiEndpoints.createUrlWithPathVariables("onboarding", "rate");
    const response = await onboardingApiClient.post(url, data);
    return response.data;
  },

  updateRate: async (data: any, id: number): Promise<any> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "onboarding",
      `rate/${id}`,
    );
    const response = await onboardingApiClient.put(url, data);
    return response.data;
  },

  getTarrifList: async (
    remoteUtilityId: string,
    filters: tarrifFilters = {},
  ): Promise<PaginatedResponse<TariffListItem>> => {
    console.log("filters", filters);
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "rate-v1",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("page", (filters.page || 1).toString());
        query.push("limit", (filters.limit || 50).toString());

        if (filters.search_data) query.push("search_data", filters.search_data);
        if (filters.utilityService) {
          query.push("utility_service", filters.utilityService);
        }
        if (filters.createdBy) {
          query.push("created_by", filters.createdBy);
        }

        if (filters.rateType) {
          query.push("rate_type", filters.rateType);
        }
        if (filters.status?.length) {
          filters.status.forEach((status) =>
            query.push("status", status.toString()),
          );
        }
      },
    );
    const response = await onboardingApiClient.get(url);
    return transformJsonToObject<PaginatedResponse<TariffListItem>>(
      response.data,
    );
  },

  getPlanVersionList: async ({
    remoteUtilityId,
    planId,
    page,
    pageSize,
  }: {
    remoteUtilityId: string;
    planId: number;
    page: number;
    pageSize: number;
  }): Promise<PlanVersionHistoryResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      `plan-history/${planId}`,
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("page", page.toString());
        query.push("limit", pageSize.toString());
      },
    );

    const response = await onboardingApiClient.get(url);

    // ✅ Manual transformation to preserve string values in changes
    const transformedData = {
      ...response.data,
      result:
        response.data.result?.map((version: any) => ({
          ...version,
          lastModifiedDate:
            version.last_modified_date || version.lastModifiedDate,
          lastModifiedUserRemoteName:
            version.last_modified_user_remote_name ||
            version.lastModifiedUserRemoteName,
          changes:
            version.changes?.map((change: any) => ({
              field: change.field,
              // Preserve actual values without transformation that converts strings to 0
              newValue: change.new_value ?? change.newValue,
              oldValue: change.old_value ?? change.oldValue,
            })) || [],
        })) || [],
    };

    return transformedData as PlanVersionHistoryResponse;
  },

  getPlanList: async (remoteUtilityId: string, filters: planFilters) => {
    const isSearching =
      filters.search_data && filters.search_data.trim() !== "";
    const endpoint = isSearching ? "search/plan" : "plan";
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      endpoint,
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("is_pagination_required", "1");
        query.push("is_draft", "1");
        query.push("page", (filters.page || 1).toString());
        query.push("limit", (filters.limit || 50).toString());

        // Add search functionality when searching
        if (isSearching) {
          query.push("search_field", "plan_name");
          query.push("search_data", filters.search_data);
        }

        // Keep existing filters
        if (filters.department) query.push("department", filters.department);
        if (filters.role) query.push("role", filters.role);
        if (filters.status?.length) {
          filters.status.forEach((status) =>
            query.push("status", status.toString()),
          );
        }
        if (filters.category) query.push("category", filters.category);
        if (filters.utilityService)
          query.push("utility_service", filters.utilityService);
      },
    );
    const response = await onboardingApiClient.get(url);
    return response.data ?? [];
  },

  addPlan: async (data: any): Promise<any> => {
    const url = ApiEndpoints.createUrlWithPathVariables("onboarding", "plan");
    const response = await onboardingApiClient.post(url, data);
    return response.data;
  },

  updatePlan: async (data: any, id: number): Promise<any> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "onboarding",
      `v1/plan/${id}`,
    );
    const response = await onboardingApiClient.put(url, data);
    return response.data;
  },

  getCategories: async (remoteUtilityId: string): Promise<CategoryOption[]> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "config-map/category_json",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("config_level", "category");
      },
    );
    const response = await onboardingApiClient.get(url);
    return transformJsonToObject<CategoryOption[]>(response.data.result);
  },

  getSubcategories: async (
    remoteUtilityId: string,
    categoryCode: string,
  ): Promise<SubcategoryOption[]> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "config-map/category_json",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("config_level", "sub_category");
        query.push("code_list", categoryCode);
      },
    );
    const response = await onboardingApiClient.get(url);
    return transformJsonToObject<SubcategoryOption[]>(response.data.result);
  },
  getEntityTypesWithoutPagination: async (
    remoteUtilityId: string,
    level: string,
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "config-map/territory_json",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("config_level", level);
        query.push("is_pagination_required", "false");
      },
    );
    const response = await onboardingApiClient.get(url);
    return transformJsonToObject<SubcategoryOption[]>(response.data.result);
  },

  // getTypeWiseRate: async (
  //   remoteUtilityId: string,
  //   rateTypeValue: any,
  //   rateType: string,

  // ): Promise<TarrifDetails[]> => {
  //   const url = ApiEndpoints.createUrlWithQueryParameters(
  //     "onboarding",
  //     "rate",
  //     (query) => {
  //       query.push("remote_utility_id", remoteUtilityId);
  //       query.push("is_active", 1);
  //       query.push(rateType, rateTypeValue);
  //     }
  //   );
  //   const response = await onboardingApiClient.get(url);
  //   return transformJsonToObject<TarrifDetails[]>(response.data.result);
  // },

  getTypeWiseRate: async (
    remoteUtilityId: string,
    rateTypeValue: any,
    rateType: string,
    utilityService?: string,
  ): Promise<TarrifDetails[]> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "rate",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push(rateType, rateTypeValue);
        query.push("utility_service", utilityService);
      },
    );
    const response = await onboardingApiClient.get(url);
    return transformJsonToObject<TarrifDetails[]>(response.data.result);
  },

  getServiceCharges: async (
    remoteUtilityId: string,
  ): Promise<ServiceCharge[]> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "config-map/service_charges_json",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
      },
    );
    const response = await onboardingApiClient.get(url);
    return transformJsonToObject<ServiceCharge[]>(response.data.result);
  },

  getTerritoryCount: async (
    remoteUtilityId: string,
  ): Promise<TerritoryCountResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "territory-count",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
      },
    );
    const response = await onboardingApiClient.get(url);
    return transformJsonToObject<TerritoryCountResponse>(response.data);
  },

  // getTerritoryMapping: async (
  //   remoteUtilityId: string,
  //   page = 1,
  //   limit = 10,
  //   territoryCode?: string,
  //   searchData?: string,
  //   isPaginationRequired?: string
  // ): Promise<PaginatedResponse<TerritoryMappingItem>> => {
  //   const url = ApiEndpoints.createUrlWithQueryParameters(
  //     "onboarding",
  //     "territory-mapping",
  //     (query) => {
  //       query.push("remote_utility_id", remoteUtilityId);
  //       query.push("page", page.toString());
  //       query.push("limit", limit.toString());
  //       query.push("is_pagination_required", isPaginationRequired);

  //       if (searchData) {
  //         query.push("search_data", searchData);
  //       }

  //       if (territoryCode) {
  //         query.push("territory_code", territoryCode);
  //       }
  //     }
  //   );

  //   const response = await onboardingApiClient.get(url);
  //   return transformJsonToObject<PaginatedResponse<TerritoryMappingItem>>(
  //     response.data
  //   );
  // },

  getTerritoryMapping: async (
    remoteUtilityId: string,
    page = 1,
    limit = 10,
    territoryCode?: string,
    searchData?: string,
    isPaginationRequired?: string,
    filters?: EntityFilters, // Added filters parameter
  ): Promise<PaginatedResponse<TerritoryMappingItem>> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "territory-mapping",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("page", page.toString());
        query.push("limit", limit.toString());
        query.push("is_pagination_required", isPaginationRequired);

        if (searchData) {
          query.push("search_data", searchData);
        }

        if (territoryCode) {
          query.push("territory_code", territoryCode);
        }

        // Add filter parameters
        if (filters) {
          if (filters.territoryType) {
            query.push("territory_type", filters.territoryType.toLowerCase());
          }

          if (filters.status !== undefined) {
            query.push("status", filters.status.toString());
          }

          if (filters.createdOn) {
            query.push("created_on", filters.createdOn);
          }

          // Override searchData if filters.search_data is provided
          if (filters.search_data) {
            query.push("search_data", filters.search_data);
          }
        }
      },
    );

    const response = await onboardingApiClient.get(url);
    console.log("API response", response.data);
    return response.data;
  },

  exportTerritoryMapping: async (
    remoteUtilityId: string,
    page = 1,
    limit = 10,
    territoryCode?: string,
    searchData?: string,
    isPaginationRequired?: string,
    filters?: EntityFilters,
  ): Promise<Blob> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "territory-mapping",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("page", page.toString());
        query.push("limit", limit.toString());
        query.push("is_pagination_required", isPaginationRequired);
        query.push("is_download", "1");

        if (searchData) {
          query.push("search_data", searchData);
        }

        if (territoryCode) {
          query.push("territory_code", territoryCode);
        }

        if (filters) {
          if (filters.territoryType) {
            query.push("territory_type", filters.territoryType.toLowerCase());
          }
          if (filters.status !== undefined) {
            query.push("status", filters.status.toString());
          }
          if (filters.createdOn) {
            query.push("created_on", filters.createdOn);
          }
          if (filters.search_data) {
            query.push("search_data", filters.search_data);
          }
        }
      },
    );

    const response = await onboardingApiClient.get(url, {
      responseType: "blob",
    });
    return response.data;
  },

  // Add territory types endpoint (if needed)
  getTerritoryTypes: async (remoteUtilityId: string) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "territory-types",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
      },
    );
    const response = await onboardingApiClient.get(url);
    return transformJsonToObject(response.data);
  },

  getRecentlyUpdatedPlans: async (
    remoteUtilityId: string,
    limit = 3,
  ): Promise<PaginatedResponse<RecentlyUpdatedPlan>> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "recently-updated/plan",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("page", "1"); // or remove if backend ignores it
        query.push("limit", limit.toString());
      },
    );
    const response = await onboardingApiClient.get(url);
    return transformJsonToObject<PaginatedResponse<RecentlyUpdatedPlan>>(
      response.data,
    );
  },

  getRecentlyUpdatedRates: async (
    remoteUtilityId: string,
    limit = 3,
  ): Promise<PaginatedResponse<RecentlyUpdatedRate>> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "recently-updated/rate",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("page", "1"); // or remove if unnecessary
        query.push("limit", limit.toString());
      },
    );
    const response = await onboardingApiClient.get(url);
    return transformJsonToObject<PaginatedResponse<RecentlyUpdatedRate>>(
      response.data,
    );
  },

  getPlanDashboardData: async (
    remoteUtilityId: string,
  ): Promise<PlanDashboardData> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "plan/dashboard",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
      },
    );
    const response = await onboardingApiClient.get(url);
    return transformJsonToObject<PlanDashboardData>(response.data.result);
  },

  getRateDetail: async (
    id: number,
    remoteUtilityId: string,
  ): Promise<RateDetail> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "onboarding",
      "rate-detail",
      [id],
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
      },
    );
    const response = await onboardingApiClient.get(url);
    const rawResult = response.data.result;

    // Transform the main object structure
    const transformedResult = transformJsonToObject<RateDetail>(rawResult);

    // Manually handle transaction_data to preserve dynamic keys in changes
    if (rawResult.transactionData && Array.isArray(rawResult.transactionData)) {
      transformedResult.transactionData = rawResult.transactionData.map(
        (item: any) => ({
          ...transformJsonToObject<any>(item),
          // Persist the original changes object to avoid property leakage
          // from the transformer's analysis of the first array element
          changes: item.changes,
        }),
      );
    }

    return transformedResult;
  },

  getRolesPermissionnData: async (
    remoteUtilityId: string,
  ): Promise<DepartmentRole[]> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "auth",
      "role-configuration",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
      },
    );
    const response = await onboardingApiClient.get(url);
    return transformJsonToObject<DepartmentRole[]>(response.data.result);
  },
  updateRoleConfiguration: async (data: any): Promise<any> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "auth",
      "role-configuration",
    );
    const response = await authApiClient.patch(url, data);
    return response.data;
  },
  updateEntityData: async (
    id: number,
    data: any,
    remoteUtilityId: string,
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "onboarding",
      `data-upload-territory-edit/${id}`,
      [],
      (query: QueryStringParameters) => {
        query.push("remote_utility_id", remoteUtilityId);
      },
    );

    const response = await authApiClient.patch(url, data);
    return response.data;
  },

  getContractsData: async (
    remoteUtilityId: string,
    categories?: string[],
    subCategories?: string[],
  ): Promise<ContractItems[]> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "contract",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        if (categories) {
          query.push("categories", categories);
        }
        if (subCategories) {
          query.push("sub_categories", subCategories);
        }
      },
    );
    const response = await onboardingApiClient.get(url);
    return transformJsonToObject<ContractItems[]>(response.data.result);
  },

  getEntityData: async (
    remoteUtilityId: string,
    territoryCode?: string,
  ): Promise<ContractItems[]> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "territory-premise-data",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("territory_code", territoryCode);
      },
    );
    const response = await onboardingApiClient.get(url);
    return transformJsonToObject<ContractItems[]>(response.data.result);
  },

  getServiceAreaStats: async (
    remoteUtilityId: string,
  ): Promise<EntityStatsResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "service-area-stats",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
      },
    );
    const response = await onboardingApiClient.get(url);
    return transformJsonToObject<EntityStatsResponse>(response.data.result);
  },

  getUserNames: async (remoteUtilityId: string): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "auth",
      "user-name",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
      },
    );
    const response = await onboardingApiClient.get(url);
    return transformJsonToObject<any>(response.data.result);
  },

  getauditLogs: async (
    remoteUtilityId: string,
    filters: any = {},
  ): Promise<PaginatedResponse<any>> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "activityLog",
      "logs",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("module", "auth");

        // Add pagination parameters
        query.push("page", (filters.page || 1).toString());
        query.push("limit", (filters.limit || 50).toString());

        // Add search parameter
        if (filters.search_data) {
          query.push("search_data", filters.search_data);
        }

        // Add action filter
        if (filters.action) {
          query.push("action", filters.action);
        }

        // Add entity filter
        if (filters.entity) {
          query.push("entity", filters.entity);
        }

        // Add date range filters
        if (filters.date_from) {
          query.push("date_from", filters.date_from);
        }

        if (filters.date_to) {
          query.push("date_to", filters.date_to);
        }
      },
    );

    const response = await onboardingApiClient.get(url);
    return transformJsonToObject<PaginatedResponse<any>>(response.data);
  },

  downloadTemplate: async (
    dataTypeName: string,
    entityType: any,
  ): Promise<Blob> => {
    console.log("entityType", entityType);
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "new/download-sample-template",
      (query) => {
        query.push("data_type_name", dataTypeName);
        query.push(
          "entity_type",
          entityType ? String(entityType).toLowerCase() : "default",
        );
      },
    );

    const response = await onboardingApiClient.get(url, {
      responseType: "blob",
    });

    return response.data;
  },
  validateData: async (
    entityType: number,
    remoteUtilityId: string,
    tenantId: string,
    file: File,
  ): Promise<any> => {
    const formData = new FormData();
    formData.append("entity_type", entityType.toString());
    formData.append("remote_utility_id", remoteUtilityId);
    formData.append("tenant_id", tenantId);
    formData.append("file", file);

    const url = ApiEndpoints.createUrl("onboarding", "territory-validate-data");
    const response = await onboardingApiClient.post(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },

  getCreatedUsers: async (remoteUtilityId: string): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "choices/rate_user",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
      },
    );
    const response = await onboardingApiClient.get(url);
    return response.data.result;
  },

  uploadData: async (
    dataUploadId: number,
    remoteUtilityId: string,
    tenantId: string,
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "onboarding",
      "territory-upload-data",
    );

    const payload = {
      data_upload_id: dataUploadId,
      remote_utility_id: remoteUtilityId,
      remote_tenant_id: tenantId,
    };

    const response = await onboardingApiClient.post(url, payload);
    return response.data;
  },

  getMonthlySummary: async (
    remoteUtilityId: string,
  ): Promise<MonthlySummaryItem[]> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "monthly-summary",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
      },
    );

    const response = await onboardingApiClient.get(url);
    return transformJsonToObject<MonthlySummaryItem[]>(response.data.result);
  },

  // Updated API getItems function
  getItems: async <T = any>({
    remoteUtilityId,
    configLevel,
    mapType,
    codeList,
    showInactive,
    isPaginationRequired,
    filters = {},
  }: GetItemsParams): Promise<PaginatedResponse<T>> => {
    // ✅ Check if search_data exists to decide which API to use
    if (filters.search_data && filters.search_data.trim()) {
      // 🔍 Use SEARCH API when search_data is provided
      const searchField = `territory_json-${configLevel}`; // Dynamic search field based on config level

      const searchUrl = ApiEndpoints.createUrlWithQueryParameters(
        "onboarding",
        "search/onboarding_config",
        (query) => {
          query.push("remote_utility_id", remoteUtilityId);
          query.push("search_field", searchField);
          query.push("search_data", filters.search_data);

          // Add pagination if needed (test if search API supports these)
          if (isPaginationRequired) {
            query.push("page", (filters.page || 1).toString());
            query.push("limit", (filters.limit || 50).toString());
            query.push("is_pagination_required", "true");
          }

          if (filters.show_inactive !== undefined) {
            query.push("status", filters.show_inactive ? "true" : "false");
          }

          if (filters.type_filter) {
            query.push("type_filter", filters.type_filter);
            query.push("unit_type", filters.type_filter);
          }

          if (filters.sort_by) {
            query.push("sort_by", filters.sort_by);
          }

          if (filters.sort_order) {
            query.push("sort_order", filters.sort_order);
          }
        },
      );

      console.log("🔍 Using SEARCH API:", searchUrl);
      const response = await onboardingApiClient.get(searchUrl);
      return response.data;
    } else {
      // 📋 Use REGULAR CONFIG-MAP API when no search_data
      const endpointKey = ConfigMapKeys[mapType];
      const regularUrl = ApiEndpoints.createUrlWithQueryParameters(
        "onboarding",
        `config-map/${endpointKey}`,
        (query) => {
          query.push("remote_utility_id", remoteUtilityId);
          query.push("config_level", configLevel);

          query.push(
            "is_pagination_required",
            isPaginationRequired ? "true" : "false",
          );
          query.push("page", (filters.page || 1).toString());
          query.push("limit", (filters.limit || 50).toString());

          if (codeList) {
            query.push("code_list", codeList);
          }

          // Handle status filter
          if (filters.show_inactive !== undefined) {
            query.push("status", filters.show_inactive ? "true" : "false");
          } else if (showInactive) {
            query.push("status", showInactive);
          }

          // Add created_by filter if provided
          if (filters.created_by) {
            query.push("created_by", filters.created_by);
          }

          // Add parent_name filter if provided
          if (filters.parent_name && filters.parent_name !== "all") {
            query.push("parent_code", filters.parent_name);
          }

          // Add type_filter if provided
          if (filters.type_filter) {
            query.push("type_filter", filters.type_filter);
            query.push("unit_type", filters.type_filter);
            query.push("type", filters.type_filter);
          }

          // Add sort_by if provided
          if (filters.sort_by) {
            query.push("sort_by", filters.sort_by);
            query.push(
              "ordering",
              filters.sort_order === "desc"
                ? `-${filters.sort_by}`
                : filters.sort_by,
            );
          }

          // Add sort_order if provided
          if (filters.sort_order) {
            query.push("sort_order", filters.sort_order);
          }
        },
      );

      console.log("📋 Using REGULAR API:", regularUrl);
      const response = await onboardingApiClient.get(regularUrl);
      return response.data;
    }
  },
  // getItems: async <T = any>({
  //   remoteUtilityId,
  //   configLevel,
  //   mapType,
  //   codeList,
  //   showInactive,
  //   filters = {}, // default to empty
  // }: GetItemsParams): Promise<T> => {
  //   const endpointKey = ConfigMapKeys[mapType];
  //   const url = ApiEndpoints.createUrlWithQueryParameters(
  //     "onboarding",
  //     `config-map/${endpointKey}`,
  //     (query) => {
  //       query.push("remote_utility_id", remoteUtilityId);
  //       query.push("config_level", configLevel);

  //       if (codeList) query.push("code_list", codeList);
  //       if (showInactive) query.push("show_inactive", true);

  //       // Optional filters
  //       if (filters.page) query.push("page", filters.page.toString());
  //       if (filters.limit) query.push("limit", filters.limit.toString());
  //       if (filters.search_data) query.push("search_data", filters.search_data);
  //       if (filters.department) query.push("department", filters.department);
  //       if (filters.role) query.push("role", filters.role);
  //       if (filters.status) {
  //         query.push("show_inactive ", filters.status);
  //       }
  //     }
  //   );

  //   const response = await onboardingApiClient.get(url);
  //   return response.data.result;
  // },

  // API functions - same pattern as getItems
  postItem: async (params: any): Promise<any> => {
    const { mapType, ...payload } = params;
    const endpointKey = ConfigMapKeys[mapType];
    const url = ApiEndpoints.createUrl(
      "onboarding",
      `config-map/${endpointKey}`,
    );
    const response = await onboardingApiClient.post(url, payload);
    return response.data.result;
  },

  patchItem: async (params: any): Promise<any> => {
    const { mapType, ...payload } = params;

    const endpointKey = ConfigMapKeys[mapType];
    const url = ApiEndpoints.createUrl(
      "onboarding",
      `config-map/${endpointKey}`,
    );
    const response = await onboardingApiClient.patch(url, payload);
    return response.data;
  },
  updateNumberFormat: async (id: string | number, data: any): Promise<any> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "auth",
      `number-format/${id}`,
    );
    const response = await onboardingApiClient.put(url, data);
    return response.data;
  },

  toggleContractStatus: async (
    id: number,
    isActive: boolean,
  ): Promise<Utility> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "onboarding",
      `contract/${id}`,
    );
    const response = await onboardingApiClient.put(url, {
      is_active: isActive,
    });
    return response.data;
  },

  getPlanDetail: async (remoteUtilityId: string, planId: any): Promise<any> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "onboarding",
      "plan-detail",
      [planId],
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("is_consumer_count", 1);
      },
    );
    const response = await onboardingApiClient.get(url);
    return transformJsonToObject<any>(response.data);
  },

  createParameter: async (data: any): Promise<any> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "onboarding",
      "v1/parameter",
    );

    const response = await onboardingApiClient.post(url, data);
    return response.data;
  },

  getParameter: async (
    utilityId: string,
    entityType?: string,
    status?: string,
    search?: string,
    page: number = 1,
    limit: number = 10,
  ): Promise<PaginatedResponse<ParameterResponse>> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "v1/parameter",
      (query) => {
        query.push("remote_utility_id", utilityId);
        query.push("is_pagination_required", "1");
        query.push("page", page.toString());
        query.push("page_size", limit.toString());
        if (entityType) {
          query.push("entity_type", entityType);
        }
        if (status) {
          query.push("active_only", status);
        }
        if (search) {
          query.push("search", search);
        }
      },
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },

  updateParameter: async (id: string, data: any): Promise<any> => {
    const url = ApiEndpoints.createUrlWithPathVariables("onboarding", `v1`, [
      "parameter",
      id,
    ]);
    const response = await onboardingApiClient.put(url, data);
    return response.data;
  },

  getParameterSummary: async (
    remoteUtilityId: string,
  ): Promise<{ total: number; active: number; inactive: number }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "parameter-summary",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
      },
    );
    const response = await onboardingApiClient.get(url);
    return response.data.result;
  },

  getEntityTypesForParameters: async () => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "onboarding",
      "choices",
      ["parameter_entity_type"],
    );
    const response = await onboardingApiClient.get(url);
    return response.data.result;
  },

  getSourceModules: async (utilityId: string) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "choices/source_module",
      (query) => {
        query.push("remote_utility_id", utilityId);
      },
    );
    const response = await onboardingApiClient.get(url);
    return response.data.result ?? [];
  },

  getValuesForSource: async (utilityId: string, id: string) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "source-module-values",
      (query) => {
        query.push("remote_utility_id", utilityId);
        query.push("source_module_id", id);
      },
    );
    const response = await onboardingApiClient.get(url);
    return response.data.result ?? [];
  },
};

export const getCategory = async (utilityId: string) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/category_json",
    (query) => {
      query.push("remote_utility_id", utilityId);
      query.push("config_level", "category");
    },
  );
  const response = await onboardingApiClient.get(url);
  return response.data.result ?? [];
};

export const getSubCategory = async (utilityId: string, code_list: any[]) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/category_json",
    (query) => {
      query.push("remote_utility_id", utilityId);
      query.push("config_level", "sub_category");
      for (let code of code_list) {
        query.push("code_list", code);
      }
    },
  );

  const response = await onboardingApiClient.get(url);
  return response.data.result ?? [];
};

export const searchPlan = async (params: {
  remoteUtilityId: string;
  utilityService?: string[] | string;
  searchQuery?: string;
  page?: number;
  limit?: number;
}): Promise<{ result: any[] }> => {
  const effectiveUtilityId =
    params.remoteUtilityId || String(getRemoteUtilityId() || "");
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "plan",
    (qs) => {
      if (effectiveUtilityId) {
        qs.push("remote_utility_id", effectiveUtilityId);
      }
      if (params.utilityService) {
        const services = Array.isArray(params.utilityService)
          ? params.utilityService
          : [params.utilityService];
        services.forEach((service: string) => {
          if (service) {
            qs.push("utility_service", service);
          }
        });
      }

      if (params.searchQuery) {
        qs.push("search", params.searchQuery);
      }
      if (params.page) {
        qs.push("page", params.page.toString());
      }
      if (params.limit) {
        qs.push("limit", params.limit.toString());
      }
    },
  );

  const response = await onboardingApiClient.get(url);
  return response.data;
};

export interface MonthConsumptionRecord {
  consumption: number;
}

export interface UnitWiseGraphResult {
  currentMonthUsage: number;
  averageMonthlyUsage: number;
  highestMonthlyUsage: number;
  lowestMonthlyUsage: number;
  unit: string;
  utilityService: string;
  records: Record<string, MonthConsumptionRecord>;
}

export interface UnitWiseGraphResponse {
  result: UnitWiseGraphResult;
}

export interface UnitWiseGraphParams {
  remoteUtilityId: string | number;
  utilityService: string;
  unit: string;
  fetchLastSixRecords?: boolean;
}

export const getUnitWiseGraph = async ({
  remoteUtilityId,
  utilityService,
  unit,
  fetchLastSixRecords = true,
}: UnitWiseGraphParams): Promise<UnitWiseGraphResponse> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    "bill/unit-wise/graph",
    (query) => {
      query.push("remote_utility_id", String(remoteUtilityId));
      query.push("utility_service", utilityService);
      query.push("unit", unit);
      query.push(
        "fetch_last_six_records",
        fetchLastSixRecords ? "True" : "False",
      );
    },
  );

  const response = await bxApiClient.get(url);
  const data = response.data;
  const res = data?.result || {};

  return {
    result: {
      currentMonthUsage: res.current_month_usage ?? 0,
      averageMonthlyUsage: res.average_monthly_usage ?? 0,
      highestMonthlyUsage: res.highest_monthly_usage ?? 0,
      lowestMonthlyUsage: res.lowest_monthly_usage ?? 0,
      unit: res.unit || unit,
      utilityService: res.utility_service || utilityService,
      records: res.records || {},
    },
  };
};
